<script setup>
import {
  computed,
  h,
  onBeforeMount,
  onBeforeUnmount,
  onMounted,
  provide,
  ref,
  watch,
} from 'vue';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useOperatingProceduresStore } from 'dashboard/stores/operatingProcedures';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import MultiSelect from 'dashboard/components-next/filter/inputs/MultiSelect.vue';
import CheckBoxSelect from 'dashboard/components-next/filter/inputs/CheckBoxSelect.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import FlowCanvas from './builder/FlowCanvas.vue';
import NodeConfigPanel from './builder/NodeConfigPanel.vue';
import SimulatorPanel from './builder/SimulatorPanel.vue';
import { useSopSimulator } from './composables/useSopSimulator';
import {
  NODE_TYPES,
  cloneNode,
  createNode,
  removeEdgesForBranch,
  removeEdgesForNode,
  serializeFlow,
  validateFlow,
} from './helpers/sopFlow';
import { parseDrawioXml } from './helpers/drawioImport';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const store = useOperatingProceduresStore();
const vuexStore = useStore();

const sopId = computed(() => route.params.sopId);

const isLoaded = ref(false);
const name = ref('');
const description = ref('');
const selectedContactReasonIds = ref([]);
const selectedMarket = ref(null);
const selectedInboxes = ref([]);
const nodes = ref([]);
const edges = ref([]);
const initialViewport = ref(null);
const selectedNode = ref(null);
const canvasRef = ref(null);
const importInputRef = ref(null);

const contactReasons = useMapGetter('contactReasons/getContactReasons');
const inboxes = useMapGetter('inboxes/getInboxes');

// Hierarchical options like the automation builder's contact reason picker
// (see automationHelper.js#mapReason).
const mapReasonOption = reason => ({
  id: reason.id,
  name: reason.name,
  additionalInfo: {
    children: (reason.child_reasons ?? []).map(mapReasonOption),
    subIds: reason.sub_ids,
  },
  icon: h('span', {
    class: 'i-lucide-tag',
    style: {
      color: reason.config?.color,
      height: '12px',
      width: '12px',
      flexShrink: 0,
    },
  }),
});

const contactReasonOptions = computed(() =>
  (contactReasons.value || []).map(mapReasonOption)
);

const marketOptions = computed(() =>
  [...new Set((inboxes.value || []).map(inbox => inbox.market))]
    .filter(Boolean)
    .map(m => ({ id: m, name: m }))
);

const inboxOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({ id: inbox.id, name: inbox.name }))
);

const simulator = useSopSimulator(nodes, edges);
provide('sopSimulator', simulator);

const isSimulating = computed(() => simulator.state.value.active);
const uiFlags = computed(() => store.getUIFlags);
const isSaving = computed(
  () => uiFlags.value.creatingItem || uiFlags.value.updatingItem
);

const savedSnapshot = ref('');
const currentSnapshot = () =>
  JSON.stringify({
    name: name.value,
    contactReasonIds: selectedContactReasonIds.value,
    market: selectedMarket.value?.id || null,
    inboxIds: selectedInboxes.value.map(inbox => inbox.id),
    flow: serializeFlow(nodes.value, edges.value, null),
  });
const isDirty = computed(() => currentSnapshot() !== savedSnapshot.value);

onBeforeMount(async () => {
  // Needed up front (not just when the picker opens) so automation node
  // previews resolve immediately when editing an already-configured flow.
  vuexStore.dispatch('macros/get');
  vuexStore.dispatch('getCannedResponse');
  vuexStore.dispatch('contactReasons/get');
  vuexStore.dispatch('inboxes/get');
  // Reference nodes pick from, and show names of, the other SOPs.
  store.get();

  if (sopId.value) {
    try {
      const record = await store.show(sopId.value);
      name.value = record.name;
      description.value = record.description || '';
      selectedContactReasonIds.value = record.contact_reason_ids || [];
      selectedMarket.value = record.market
        ? { id: record.market, name: record.market }
        : null;
      // MultiSelect resolves labels from options by id, so inboxes still
      // loading don't drop saved ids.
      selectedInboxes.value = (record.inbox_ids || []).map(id => ({ id }));
      nodes.value = structuredClone(record.flow?.nodes || []);
      edges.value = structuredClone(record.flow?.edges || []);
      initialViewport.value = record.flow?.viewport || null;
    } catch (error) {
      useAlert(t('SOPS.BUILDER.LOAD_ERROR'));
      router.replace({ name: 'sops_list' });
      return;
    }
  } else {
    nodes.value = [createNode(NODE_TYPES.START, { x: 80, y: 120 })];
  }
  savedSnapshot.value = currentSnapshot();
  isLoaded.value = true;
});

const onNodeSelected = node => {
  selectedNode.value = node;
};

const removeBranch = branchId => {
  const node = selectedNode.value;
  if (!node?.data.branches) return;
  node.data.branches = node.data.branches.filter(b => b.id !== branchId);
  edges.value = removeEdgesForBranch(edges.value, branchId);
};

const onDeleteNode = nodeId => {
  if (!nodeId) return;
  edges.value = removeEdgesForNode(edges.value, nodeId);
  nodes.value = nodes.value.filter(n => n.id !== nodeId);
  if (selectedNode.value?.id === nodeId) selectedNode.value = null;
};

// Nodes can also be removed from the canvas (Backspace), bypassing onDeleteNode.
watch(
  () => nodes.value.length,
  () => {
    if (!nodes.value.some(n => n.id === selectedNode.value?.id))
      selectedNode.value = null;
  }
);

const onDuplicateNode = nodeId => {
  const node = nodes.value.find(n => n.id === nodeId);
  if (!node || node.type === NODE_TYPES.START) return;
  const clone = cloneNode(node);
  nodes.value.push(clone);
  selectedNode.value = clone;
};

const toggleSimulate = () => {
  if (isSimulating.value) {
    simulator.stop();
    return;
  }
  selectedNode.value = null;
  if (!simulator.start()) useAlert(t('SOPS.SIMULATOR.NO_START'));
};

const openImportDialog = () => importInputRef.value?.click();

const onImportFile = async event => {
  const file = event.target.files[0];
  event.target.value = ''; // allow re-importing the same file
  if (!file) return;

  let result;
  try {
    result = parseDrawioXml(await file.text());
  } catch (error) {
    useAlert(t('SOPS.BUILDER.IMPORT_ERROR'));
    return;
  }

  if (isDirty.value && !window.confirm(t('SOPS.BUILDER.IMPORT_CONFIRM')))
    return;

  nodes.value = result.nodes;
  edges.value = result.edges;
  selectedNode.value = null;
  if (!name.value) name.value = file.name.replace(/\.(drawio|xml)$/i, '');

  useAlert(
    t('SOPS.BUILDER.IMPORT_SUCCESS', {
      nodes: result.nodes.length,
      edges: result.edges.length,
    })
  );
  if (result.warnings.length) {
    useAlert(
      t('SOPS.BUILDER.IMPORT_WARNINGS', { count: result.warnings.length })
    );
    result.warnings.forEach(warning =>
      window.console.warn('[SOP import]', warning)
    );
  }
};

const performSave = async ({ silent = false } = {}) => {
  const { error: validationError, warnings } = validateFlow(
    nodes.value,
    edges.value
  );
  if (validationError) {
    if (!silent) useAlert(t(`SOPS.BUILDER.VALIDATION.${validationError}`));
    return;
  }
  if (!silent) {
    warnings.forEach(warning =>
      useAlert(t(`SOPS.BUILDER.VALIDATION.${warning}`))
    );
  }

  if (!name.value) name.value = t('SOPS.BUILDER.NAME_PLACEHOLDER');
  const payload = {
    name: name.value,
    description: description.value,
    contact_reason_ids: selectedContactReasonIds.value,
    market: selectedMarket.value?.id || null,
    inbox_ids: selectedInboxes.value.map(inbox => inbox.id),
    flow: serializeFlow(
      nodes.value,
      edges.value,
      canvasRef.value?.toObject().viewport || null
    ),
  };

  // Taken with the payload so edits made while the request is in flight stay dirty.
  const snapshot = currentSnapshot();

  try {
    if (sopId.value) {
      await store.update({ id: Number(sopId.value), ...payload });
      savedSnapshot.value = snapshot;
    } else {
      const record = await store.create(payload);
      savedSnapshot.value = snapshot;
      router.replace({ name: 'sops_edit', params: { sopId: record.id } });
    }
    if (!silent) useAlert(t('SOPS.BUILDER.SAVE_SUCCESS'));
  } catch (error) {
    if (!silent) {
      useAlert(error.response?.data?.message || t('SOPS.BUILDER.SAVE_ERROR'));
    }
  }
};

const save = () => performSave();

const canSave = () =>
  isLoaded.value && !isSimulating.value && !isSaving.value && isDirty.value;

// Auto-save existing SOPs only — never auto-create records for a
// builder the designer may abandon.
const AUTO_SAVE_INTERVAL = 15000;
let autoSaveTimer = null;
onMounted(() => {
  autoSaveTimer = setInterval(() => {
    if (!sopId.value || !canSave()) return;
    performSave({ silent: true });
  }, AUTO_SAVE_INTERVAL);
});
onBeforeUnmount(() => clearInterval(autoSaveTimer));

useKeyboardEvents({
  '$mod+KeyS': {
    action: e => {
      e.preventDefault();
      if (canSave()) save();
    },
    allowOnFocusedInput: true,
  },
});

onBeforeRouteLeave(() => {
  if (!isLoaded.value || !isDirty.value) return true;
  return window.confirm(t('SOPS.BUILDER.UNSAVED_CHANGES'));
});
</script>

<template>
  <div
    class="mx-auto flex h-full min-h-0 w-full max-w-7xl flex-col gap-3 py-4 min-[2200px]:max-w-[72%]"
  >
    <div v-if="!isLoaded" class="flex flex-1 items-center justify-center">
      <Spinner />
    </div>
    <template v-else>
      <div class="flex items-center gap-3">
        <Input
          v-model="name"
          class="w-56"
          :placeholder="$t('SOPS.BUILDER.NAME_PLACEHOLDER')"
          :disabled="isSimulating"
        />
        <span class="whitespace-nowrap text-sm text-n-slate-11">
          {{ $t('SOPS.BUILDER.APPLICABLE_FOR') }}
        </span>
        <CheckBoxSelect
          v-model="selectedContactReasonIds"
          :options="contactReasonOptions"
          :placeholder="$t('SOPS.BUILDER.CONTACT_REASON_PLACEHOLDER')"
          :max-chips="1"
        />
        <span class="whitespace-nowrap text-sm text-n-slate-11">
          {{ $t('SOPS.BUILDER.IN_MARKET') }}
        </span>
        <SingleSelect
          v-model="selectedMarket"
          :options="marketOptions"
          :placeholder="$t('SOPS.BUILDER.MARKET_PLACEHOLDER')"
          placeholder-icon="i-lucide-globe"
          disable-search
        />
        <span
          v-if="$t('SOPS.BUILDER.MARKET_SUFFIX')"
          class="whitespace-nowrap text-sm text-n-slate-11"
        >
          {{ $t('SOPS.BUILDER.MARKET_SUFFIX') }}
        </span>
        <div class="flex-1" />
        <input
          ref="importInputRef"
          type="file"
          accept=".xml,.drawio"
          class="hidden"
          @change="onImportFile"
        />
        <Button
          :label="$t('SOPS.BUILDER.IMPORT')"
          icon="i-lucide-upload"
          slate
          faded
          :disabled="isSimulating"
          @click="openImportDialog"
        />
        <Button
          :label="
            isSimulating
              ? $t('SOPS.BUILDER.EXIT_SIMULATE')
              : $t('SOPS.BUILDER.SIMULATE')
          "
          :icon="isSimulating ? 'i-lucide-square' : 'i-lucide-play'"
          slate
          faded
          @click="toggleSimulate"
        />
        <Button
          :label="$t('SOPS.BUILDER.SAVE')"
          icon="i-lucide-save"
          :is-loading="isSaving"
          :disabled="isSimulating || !isDirty"
          @click="save"
        />
      </div>
      <div class="flex items-center gap-3">
        <!-- Spacer matches the name input so this row continues the sentence above. -->
        <div class="w-56 shrink-0" />
        <span class="whitespace-nowrap text-sm text-n-slate-11">
          {{ $t('SOPS.BUILDER.IN_INBOXES') }}
        </span>
        <MultiSelect
          v-model="selectedInboxes"
          :options="inboxOptions"
          :placeholder="$t('SOPS.BUILDER.INBOXES_PLACEHOLDER')"
          :max-chips="3"
        />
      </div>
      <div class="flex min-h-0 flex-1 gap-3 pb-2">
        <FlowCanvas
          ref="canvasRef"
          v-model:nodes="nodes"
          v-model:edges="edges"
          class="min-w-0 flex-1"
          :readonly="isSimulating"
          :initial-viewport="initialViewport"
          @node-selected="onNodeSelected"
          @delete-node="onDeleteNode"
          @duplicate-node="onDuplicateNode"
        />
        <NodeConfigPanel
          v-if="selectedNode && !isSimulating"
          :node="selectedNode"
          :market="selectedMarket?.id || null"
          :sop-id="sopId ? Number(sopId) : null"
          @remove-branch="removeBranch"
          @delete-node="onDeleteNode(selectedNode.id)"
        />
        <SimulatorPanel v-if="isSimulating" :simulator="simulator" />
      </div>
    </template>
  </div>
</template>
